import { useMemo, useState } from 'react';

import {
  createReview,
  deleteReview as apiDeleteReview,
} from '../api';
import { useAppConfig } from '../state/config';
import { useProfile } from '../state/profile';
import type {
  CompletedReviewStatus,
  Geography,
  Review,
  ReviewStatus,
} from '../types';
import { GEO_ALL } from '../types';

import { Modal } from './Modal';
import { STATUS_ICON, STATUS_LABEL } from './ReviewBadges';

type Row = {
  geo: Geography;
  current: ReviewStatus;
  draft: ReviewStatus;
};

/**
 * Per-geography review status editor. Users pick a new status from a dropdown
 * for each visible geography (all of them when the top bar is "All
 * geographies", otherwise just the selected one). Save diffs drafts against
 * the current state and issues one POST per newly-completed row, one DELETE
 * per row reverted to pending; Cancel throws away the drafts.
 */
export function ModifyReviewStatusDialog({
  stream,
  kbNumber,
  formSysId,
  formName,
  topBarGeo,
  reviewsByGeo,
  onClose,
  onSaved,
}: {
  stream: string;
  kbNumber: string;
  formSysId: string | null;
  formName: string | null;
  topBarGeo: string;
  reviewsByGeo: Map<string, Review> | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const cfg = useAppConfig();
  const { profile } = useProfile();

  const visibleGeos = useMemo<Geography[]>(() => {
    if (topBarGeo === GEO_ALL) return cfg.geographies;
    const only = cfg.geographies.find((g) => g.id === topBarGeo);
    return only ? [only] : [];
  }, [cfg.geographies, topBarGeo]);

  const [rows, setRows] = useState<Row[]>(() =>
    visibleGeos.map((geo) => {
      const current =
        reviewsByGeo?.get(geo.id)?.status ?? 'pending_review';
      return { geo, current, draft: current };
    }),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changedCount = useMemo(
    () => rows.filter((r) => r.draft !== r.current).length,
    [rows],
  );

  async function save() {
    setError(null);
    const changed = rows.filter((r) => r.draft !== r.current);
    if (changed.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await Promise.all(
        changed.map((r) => {
          if (r.draft === 'pending_review') {
            return apiDeleteReview(kbNumber, r.geo.id);
          }
          return createReview({
            kb_number: kbNumber,
            geography: r.geo.id,
            status: r.draft as CompletedReviewStatus,
            reviewer_name: profile?.full_name ?? 'Unknown',
            reviewer_email: profile?.email ?? null,
            stream,
            form_sys_id: formSysId,
          });
        }),
      );
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  function updateRow(geoId: string, next: ReviewStatus) {
    setRows((prev) =>
      prev.map((r) => (r.geo.id === geoId ? { ...r, draft: next } : r)),
    );
  }

  return (
    <Modal
      title="Modify review status"
      subtitle={
        <span>
          {formName ? <span className="font-medium">{formName} · </span> : null}
          <span className="font-mono text-slate-500">{kbNumber}</span>
        </span>
      }
      size="xl"
      onClose={saving ? undefined : onClose}
    >
      {rows.length === 0 ? (
        <p className="text-sm text-slate-600">
          No configured geographies to review.
        </p>
      ) : (
        <>
          <p className="text-sm text-slate-600">
            Pick a status per geography. Changes are saved together.
          </p>
          <div className="mt-3 divide-y divide-slate-100 rounded-md border border-slate-200">
            {rows.map((r) => (
              <StatusRow
                key={r.geo.id}
                row={r}
                onChange={(next) => updateRow(r.geo.id, next)}
                disabled={saving}
              />
            ))}
          </div>
        </>
      )}

      {error && (
        <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        <span className="mr-auto text-xs text-slate-500">
          {changedCount === 0
            ? 'No changes'
            : `${changedCount} change${changedCount === 1 ? '' : 's'} pending`}
        </span>
        <button className="btn-ghost h-9" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button
          className="btn-primary h-9"
          onClick={save}
          disabled={saving || rows.length === 0}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}

const STATUS_OPTIONS: ReviewStatus[] = [
  'pending_review',
  'review_completed_form_required',
  'review_completed_form_not_required',
];

function StatusRow({
  row,
  onChange,
  disabled,
}: {
  row: Row;
  onChange: (next: ReviewStatus) => void;
  disabled: boolean;
}) {
  const dirty = row.draft !== row.current;
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <div className="w-36 flex-shrink-0">
        <div className="text-sm font-medium text-slate-800 break-words">
          {row.geo.name}
        </div>
        <div className="text-[11px] font-mono text-slate-500">
          {row.geo.code}
        </div>
      </div>
      <select
        className="input h-8 text-sm flex-1 min-w-0"
        value={row.draft}
        onChange={(e) => onChange(e.target.value as ReviewStatus)}
        disabled={disabled}
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {STATUS_ICON[s]} {STATUS_LABEL[s]}
          </option>
        ))}
      </select>
      {dirty && (
        <span
          className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
          title="Unsaved change"
        >
          •
        </span>
      )}
    </div>
  );
}
